import React, {ChangeEvent, useState} from "react";
import {Helmet} from "react-helmet-async";
import {
    Grid,
    Divider as MuiDivider,
    Typography,
    Button,
    Stack,
    CardContent,
    Card,
    Alert as MuiAlert, Tabs, Tab, Chip, Avatar
} from "@mui/material";

import {Formik} from "formik";
import * as Yup from "yup";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import Loader from "../../components/Loader";
import SnackbarNotification from "../../components/SnackbarNotification";
import InlineMedia from "../../components/inline-media/InlineMedia";
import TabPanel from "../components/TabPanel";
import GeneralInfoCategory from "./fieldGroups/GeneralInfoCategory";
import SeoCategory from "./fieldGroups/SeoCategory";
import VideoCategory from "./fieldGroups/VideoCategory";


// GraphQL
import {FetchResult} from "@apollo/client";
import {
    CategoryQuery,
    CreateCategoryMutation, InlineMediaUploadMutation, NewCategoryQuery,
    useCategoryQuery, useCreateCategoryMutation, useInlineMediaUploadMutation,
    useNewCategoryQuery, useResetCategoryMutation,
    useUpdateCategoryMutation
} from "../../graphql/category/category.graphql-gen";

// Types
import {MediaItemType} from "../../types/inlineMedia";

// Icons
import {Save as SaveIcon} from "react-feather";
import {X as CloseIcon} from "react-feather";

// Styling
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";
import {spacing} from "@mui/system";
import styled from "styled-components/macro";
import {ContentElementItemType} from "../../types/inlineContentElementType";
import InlineContentElement from "../../components/inline-content-element/InlineContentElement";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Tree from "../../components/tree/Tree";
import {shallowEqual} from "react-redux";
import england from "../../assets/img/england.svg";
import germany from "../../assets/img/germany.svg";
import {InlineMediaUploadArticleMutation} from "../../graphql/article/article.graphql-gen";

const Alert = styled(MuiAlert)(spacing);
const Divider = styled(MuiDivider)(spacing);


const validationSchema = Yup.object().shape({
    title: Yup.string().required("Required"),
});

const Category: React.FC = () => {
    let title: string = 'New category'
    let dataObj: any = {};
    const params = useParams();
    let id = params.id ? params.id : '';
    const inlineModels: string[] = ['images', 'headerImages', 'files'];
    const navigate = useNavigate();
    const model: string = 'Category';
    const multipleSelect: boolean = false;
    let fromMediaCenter: boolean = false;
    const location: any = useLocation();

    // Queries
    const {data: data, error: error, loading: loading} = useCategoryQuery({
        variables: {id, model},
        skip: id === '',
        onCompleted: (data: CategoryQuery) => {
            setImages(data.category.images);
            setHeaderImages(data.category.headerImages);
            setMediaItems(data.category.images?.items);
            setHeaderImagesItems(data.category.headerImages?.items);
            setFiles(data.category.files);
            setFilesItems(data.category.files?.items);
            setContentElements(data.category.contentElements);
            if (data.category && data.category.category) {
                setState(data.category.category);
                setInicialValue({
                    "data": data.category.category,
                    "images": data.category.images?.items,
                    "headerImages": data.category.headerImages?.items,
                    "files": data.category.files?.items,
                    "contentElements": data.category.contentElements,
                    "checked": data.categoriesThree.checked
                });
            }
            setChecked(data.categoriesThree.checked);
            setExpanded(data.categoriesThree.expanded);

            setUserId(data.user.user?.id);
        }
    });
    const {data: dataNew, error: errorNew, loading: loadingNew} = useNewCategoryQuery({
        variables: {
            fields: inlineModels,
            model
        },
        skip: id !== '',
        onCompleted: (data: NewCategoryQuery) => {
            setState({});
            setInicialValue({});

            setUserId(data.user.user?.id);

            if (data.inlineMedia) {
                data.inlineMedia.map(item => {
                    switch (item.field) {
                        case 'images':
                            setImages(item.inlineMedia);
                            break;
                        case 'headerImages':
                            setHeaderImages(item.inlineMedia);
                            break;
                        case 'files':
                            setFiles(item.inlineMedia);
                            break;
                    }
                })
            }
        }
    });
    const [updateCategory] = useUpdateCategoryMutation();
    const [createCategory] = useCreateCategoryMutation();
    const [inlineMediaUpload] = useInlineMediaUploadMutation();
    const [resetCategory] = useResetCategoryMutation();

    // States
    const [updated, setUpdated] = useState(false);
    const [images, setImages] = useState<any>(dataObj.category ? dataObj.category.images : {});
    const [mediaItems, setMediaItems] = useState<any>(dataObj.category ? dataObj.category.images.items : []);
    const [headerImages, setHeaderImages] = useState<any>(dataObj.category ? dataObj.category.headerImages : {});
    const [headerImagesItems, setHeaderImagesItems] = useState<any>(dataObj.category ? dataObj.category.headerImages.items : []);
    const [files, setFiles] = useState<any>(dataObj.category ? dataObj.category.files : {});
    const [filesItems, setFilesItems] = useState<any>(dataObj.category ? dataObj.category.files.items : []);
    const [contentElements, setContentElements] = useState<any>(dataObj.category ? dataObj.category.contentElements : []);
    const [removedMedia, setRemovedMedia] = useState<any[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [state, setState] = useState<any>();
    const [tabValue, setTabValue] = useState(location.state?.submodelName ? 3 : (location.state?.fromMediaCenter ? 2 : 0));
    const [checked, setChecked] = useState(dataObj.categoriesThree ? dataObj.categoriesThree.checked : []);
    const [expanded, setExpanded] = useState(dataObj.categoriesThree ? dataObj.categoriesThree.expanded : []);
    const [open, setOpen] = useState<boolean>(false);
    const [inicialValue, setInicialValue] = useState<any>();
    const [lang, setLang] = useState<string>('');
    const [isLang, setIsLang] = useState<boolean>(false);

    const [userId, setUserId] = useState<any>();

    const [template, setTemplate] = useState(1);


    // Set loading
    if (loading || loadingNew || loader) {
        return <Loader/>
    }

    // Handle error
    if (error || errorNew) {
        return (
            <Alert mb={4} severity="error">
                Error!
            </Alert>
        );
    }

    // Set data
    if (data || dataNew) {
        dataObj = data || dataNew
    }

    // Set title
    if (dataObj.category && dataObj.category.category) {
        title = dataObj.category.category.title;
    }


    //------------------------------- Public methods -------------------------------------

    // Handle save action
    const onSaveCategory = (fieldName?: any, fieldType?: any, isMultiple?: any, submodelId?: any, submodelName?: string) => {
        if (id != '') {
            updateCategoryMutation(state, fieldName, fieldType, isMultiple, submodelId, submodelName);
        } else {
            createCategoryMutation(state, fieldName, fieldType, isMultiple, submodelId, submodelName);
        }
        setInicialValue({
            "data": state, "images": mediaItems, "headerImages": headerImagesItems, "files": filesItems,
            "contentElements": contentElements, "checked": checked
        });
    }

    // Update category mutation
    const updateCategoryMutation = (data: any, fieldName?: string, fieldType?: string, isMultiple?: boolean, submodelId?: any, submodelName?: string) => {
        updateCategory({
            variables: {
                model,
                id,
                data,
                parents: checked,
                images: mediaItems,
                headerImages: headerImagesItems,
                files: filesItems,
                removedMedia,
                contentElements,
                temp: fromMediaCenter
            }
        }).then(r => {

            setUpdated(true)
            setRemovedMedia([])
            setTimeout(() => {
                setUpdated(false);
            }, 3000)
            if (fromMediaCenter) {
                navigate(`/mediacenter/`, {
                    replace: true,
                    state: {fieldName, fieldType, isMultiple, modelName: model, modelId: id, submodelId, submodelName}
                })
            }

        }).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        });
    }

    // Create category mutation
    const createCategoryMutation = (data: any, fieldName?: string, fieldType?: string, isMultiple?: boolean, submodelId?: any, submodelName?: string) => {
        createCategory({
            variables: {
                model,
                data,
                user: userId,
                parents: checked,
                images: mediaItems,
                headerImages: headerImagesItems,
                files: filesItems,
                removedMedia,
                contentElements,
                temp: fromMediaCenter
            },
        })
            .then((res: FetchResult<CreateCategoryMutation>) => {
                if (res.data && res.data.createCategory.category) {
                    setUpdated(true);
                    if (fromMediaCenter) {
                        navigate(`/mediacenter/`, {
                            replace: true,
                            state: {
                                fieldName,
                                fieldType,
                                isMultiple,
                                modelName: model,
                                modelId: res.data.createCategory.category.id,
                                submodelId,
                                submodelName
                            }
                        })
                    } else {
                        navigate(`/categories/${res.data.createCategory.category.id}`, {replace: true})
                    }

                }
            })
            .catch(error => {
                return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
            });
    }

    //Back to ListView
    const backToListView = () => {
        resetCategory({
            variables: {
                model,
                id
            }
        }).then(r => {
            if (!isLang) {
                navigate(`/categories`, {replace: true})
            } else {
                navigate(`/categories/${lang}/${id}`, {replace: true})
            }
        }).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        });
    }
    const openDialog = () => {
        const hasChanged = !shallowEqual(inicialValue, {
            "data": state, "images": mediaItems, "headerImages": headerImagesItems, "files": filesItems,
            "contentElements": contentElements, "checked": checked
        });
        setIsLang(false);
        if (hasChanged) {
            setOpen(true);
        } else {
            backToListView()
        }
    }

    const openTranslateDialog = (lang: any) => {
        const hasChanged = !shallowEqual(inicialValue, {
            "data": state, "images": mediaItems, "headerImages": headerImagesItems, "files": filesItems,
            "contentElements": contentElements, "checked": checked
        });
        setLang(lang);
        setIsLang(true);
        if (hasChanged) {
            setOpen(true);
        } else {
            navigate(`/categories/${lang}/${id}`, {replace: true})
        }
    }
    // Close snackbar notification
    const closeDialog = () => {
        setOpen(false);
    }

    // Add media
    const onAddMediaHandler = (files: any) => {
        setLoader(true);
        inlineMediaUpload({
            variables: {
                id,
                model,
                field: 'images',
                images: files
            }
        }).then((res: FetchResult<InlineMediaUploadMutation>) => {
                const addedFiles = res.data?.inlineMediaUpload;
                setMediaItems((prevState: any) => {
                    if (addedFiles) {
                        return [...prevState, ...addedFiles];
                    }
                });
                setLoader(false);
            }
        ).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        })
    }
    const onAddHeaderImagesHandler = (files: any) => {
        setLoader(true);
        inlineMediaUpload({
            variables: {
                id,
                model,
                field: 'headerImages',
                images: files
            }
        }).then((res: FetchResult<InlineMediaUploadMutation>) => {
                const addedFiles = res.data?.inlineMediaUpload;
                setHeaderImagesItems((prevState: any) => {
                    if (addedFiles) {
                        return [...prevState, ...addedFiles];
                    }
                });
                setLoader(false);
            }
        ).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        })
    }
    const onAddFilesHandler = (files: any) => {
        setLoader(true);
        inlineMediaUpload({
            variables: {
                id,
                model,
                field: 'files',
                images: files
            }
        }).then((res: FetchResult<InlineMediaUploadArticleMutation>) => {
                const addedFiles = res.data?.inlineMediaUpload;
                setFilesItems((prevState: any) => {
                    if (addedFiles) {
                        return [...prevState, ...addedFiles];
                    }
                });
                setLoader(false);
            }
        ).catch(error => {
            return <SnackbarNotification message="Error, please try again later." open={true} type={'error'}/>
        })
    }

    // Update media
    const onMediaUpdateHandler = (updateData: MediaItemType) => {
        const foundIndex = mediaItems.findIndex((item: MediaItemType) => item.id == updateData.id);
        mediaItems[foundIndex] = updateData;
        setMediaItems(mediaItems)
    }
    const onHeaderImagesUpdateHandler = (updateData: MediaItemType) => {
        const foundIndex = headerImagesItems.findIndex((item: MediaItemType) => item.id == updateData.id);
        headerImagesItems[foundIndex] = updateData;
        setHeaderImagesItems(headerImagesItems)
    }
    const onFilesUpdateHandler = (updateData: MediaItemType) => {
        const foundIndex = filesItems.findIndex((item: MediaItemType) => item.id == updateData.id);
        filesItems[foundIndex] = updateData;
        setFilesItems(filesItems)
    }

    // Remove media
    const onMediaRemoveHandler = (id: number | string) => {
        setRemovedMedia(prevState => [...prevState, id])
        setMediaItems((prevState: MediaItemType[]) => prevState.filter(item => item.id !== id));
    }
    const onHeaderImagesRemoveHandler = (id: number | string) => {
        setRemovedMedia(prevState => [...prevState, id])
        setHeaderImagesItems((prevState: MediaItemType[]) => prevState.filter(item => item.id !== id));
    }
    const onFilesRemoveHandler = (id: number | string) => {
        setRemovedMedia(prevState => [...prevState, id])
        setFilesItems((prevState: MediaItemType[]) => prevState.filter(item => item.id !== id));
    }

    const onMediaBrowseHandler = () => {
        fromMediaCenter = true;
        onSaveCategory('images', images.fieldType, images.isMultiple);
    }
    const onHeaderImagesBrowseHandler = () => {
        fromMediaCenter = true;
        onSaveCategory('headerImages', headerImages.fieldType, headerImages.isMultiple);
    }
    const onFilesBrowseHandler = () => {
        fromMediaCenter = true;
        onSaveCategory('files', files.fieldType, files.isMultiple);
    }

    // This function is called when the select changes
    const inputHandler = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>, field?: string, type?: string, values?: any[]) => {
        let fieldName = '';
        let value: any;

        if (type) {

            // @ts-ignore
            fieldName = field;
            value = values
        } else {

            if (event.target) {
                fieldName = event.target.name;
                value = event.target.value;
            }

            if (field) {
                fieldName = field
                value = event
            }
        }
        // Update select value
        setState({...state, [fieldName]: value});

    };


    //------------------------ Inline content element methods --------------------------

    // Add content element
    const onAddContentElementHandler = (element: ContentElementItemType) => {
        setContentElements((prevState: any) => {
            if (element) {
                return [...prevState, element];
            }
        });
    }

    // Update content element
    const onContentElementUpdateHandler = (updateData: ContentElementItemType) => {
        // const foundIndex = contentElements.findIndex((item: ContentElementItemType) => item.id == updateData.id);
        // contentElements[foundIndex] = updateData;
        const updateContentElements = contentElements.map((el: ContentElementItemType) => {
            if (el.id == updateData.id) {
                return updateData
            } else {
                return el;
            }
        });

        setContentElements(updateContentElements);
    }

    // Remove content element
    const onContentElementRemoveHandler = (id: number | string) => {
        setContentElements((prevState: ContentElementItemType[]) => prevState.filter(item => item.id !== id));
    }

    // Remove media from content element
    const onContentElementMediaRemoveHandler = (id: number | string) => {
        setRemovedMedia(prevState => [...prevState, id])
    }

    // Brows media for content element
    const onContentElementMediaBrowseHandler = (item: any) => {
        fromMediaCenter = true;
        onSaveCategory('images', 'image', false, item.id, 'ContentElement');
    }


    // Handle tab change
    const handleTabChange = (event: any, newValue: any) => {
        setTabValue(newValue);
    };

    // Set tub properties
    const tabsProps = (index: number) => {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    //Set tree handlerers
    const treeHandlerCheck = (checkedArr: any, targetNode: any) => {
        if (multipleSelect) {
            setChecked(checkedArr)
        } else {
            if (checkedArr.length != 0) {
                setChecked([targetNode.value.toString()])
            } else {
                setChecked([]);
            }
        }
    }
    const treeHandlerExpand = (event: any) => {
        setExpanded(event)
    }


    // const templateChange = (event: any) => {
    //     setTemplate(event.target.value);
    //
    //     setImages(dataObj.category.images);
    //     setHeaderImages(dataObj.category.headerImages);
    //     setMediaItems(dataObj.category.images?.items);
    //     setHeaderImagesItems(dataObj.category.headerImages?.items);
    //     setContentElements(dataObj.category.contentElements);
    //     if (dataObj.category && dataObj.category.category) {
    //         setState(dataObj.category.category);
    //     }
    //     setChecked(dataObj.categoriesThree.checked);
    //     setExpanded(dataObj.categoriesThree.expanded);
    //
    // };


    return (
        <React.Fragment>
            <Helmet title="Category"/>

            <Grid justifyContent="space-between" container spacing={10}>
                <Grid item>
                    <Typography variant="h3" gutterBottom display="inline">
                        {title}
                    </Typography>
                </Grid>
                <Grid item>
                    <Stack direction="row" spacing={2}>
                        {id &&
                            <div>
                                <Chip className="lang-nav active" variant="outlined" avatar={<Avatar src={england}/>}
                                      label="En"/>
                                <Chip className="lang-nav" variant="outlined" onClick={(lang) => {
                                    openTranslateDialog('de')
                                }} clickable={true} avatar={<Avatar src={germany}/>} label="De"/>
                            </div>
                        }

                        <Button variant="contained" color="inherit" onClick={openDialog}>
                            <CloseIcon/>
                        </Button>

                        <Button type="submit" variant="contained" color="primary" form="single-form">
                            <SaveIcon/>
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <Divider my={6}/>

            {state &&
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Formik
                                    initialValues={state}
                                    validationSchema={validationSchema}
                                    onSubmit={onSaveCategory}
                                    validateOnChange={true}
                                    validateOnMount={true}
                                >
                                    {({
                                          errors,
                                          handleBlur,
                                          handleChange,
                                          handleSubmit,
                                          touched,
                                          values,
                                      }) => (
                                        <form onSubmit={handleSubmit} id="single-form">
                                            <div className="tabs-wrapper">
                                                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable"
                                                      allowScrollButtonsMobile>
                                                    <Tab label="General" {...tabsProps(0)} />
                                                    <Tab label="Parent category" {...tabsProps(1)} />
                                                    <Tab label="Media" {...tabsProps(2)} />
                                                    <Tab label="Promotions" {...tabsProps(3)} />
                                                </Tabs>
                                            </div>
                                            <div className="scrollable-content">
                                                <TabPanel value={tabValue} index={0}>
                                                    {/*<Grid container spacing={6}>*/}
                                                    {/*    <Grid item xs={12} md={6} mb={5}>*/}
                                                    {/*        <FormControl fullWidth>*/}
                                                    {/*            <InputLabel>Template</InputLabel>*/}
                                                    {/*            <Select*/}
                                                    {/*                value={template}*/}
                                                    {/*                label="Template"*/}
                                                    {/*                onChange={templateChange}*/}
                                                    {/*            >*/}
                                                    {/*                <MenuItem value={1}>Template 1</MenuItem>*/}
                                                    {/*                <MenuItem value={2}>Template 2</MenuItem>*/}
                                                    {/*                <MenuItem value={3}>Template 3</MenuItem>*/}
                                                    {/*            </Select>*/}
                                                    {/*        </FormControl>*/}
                                                    {/*    </Grid>*/}
                                                    {/*</Grid>*/}

                                                    <GeneralInfoCategory
                                                        data={dataObj}
                                                        onChange={(e, field, type, values) => {
                                                            inputHandler(e, field, type, values);
                                                            handleChange(e);
                                                        }}
                                                        touched={touched}
                                                        errors={errors}
                                                        values={values}
                                                        state={state}
                                                    />


                                                </TabPanel>
                                                <TabPanel value={tabValue} index={1}>

                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" mb={5}>
                                                                Parent category
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Tree
                                                        data={dataObj.categoriesThree}
                                                        checked={checked}
                                                        expanded={expanded}
                                                        onCheck={treeHandlerCheck}
                                                        onExpand={treeHandlerExpand}/>
                                                </TabPanel>
                                                <TabPanel value={tabValue} index={2}>
                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" mb={5}>
                                                                Images
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <InlineMedia
                                                        items={mediaItems}
                                                        fieldType={images.fieldType}
                                                        allowedExtensions={images.allowedExtensions}
                                                        isMultiple={images.isMultiple}
                                                        onAdd={onAddMediaHandler}
                                                        onUpdate={onMediaUpdateHandler}
                                                        onDelete={onMediaRemoveHandler}
                                                        isBrowseMedia={true}
                                                        browseMedia={onMediaBrowseHandler}
                                                    />
                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Divider mb={10}/>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" mb={5}>
                                                                Header Image
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <InlineMedia
                                                        items={headerImagesItems}
                                                        fieldType={headerImages.fieldType}
                                                        allowedExtensions={headerImages.allowedExtensions}
                                                        isMultiple={headerImages.isMultiple}
                                                        onAdd={onAddHeaderImagesHandler}
                                                        onUpdate={onHeaderImagesUpdateHandler}
                                                        onDelete={onHeaderImagesRemoveHandler}
                                                        isBrowseMedia={true}
                                                        browseMedia={onHeaderImagesBrowseHandler}
                                                    />

                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Divider mb={10}/>
                                                        </Grid>
                                                    </Grid>

                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" mb={5}>
                                                                Files
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <InlineMedia
                                                        items={filesItems}
                                                        fieldType={files.fieldType}
                                                        allowedExtensions={files.allowedExtensions}
                                                        isMultiple={files.isMultiple}
                                                        onAdd={onAddFilesHandler}
                                                        onUpdate={onFilesUpdateHandler}
                                                        onDelete={onFilesRemoveHandler}
                                                        isBrowseMedia={true}
                                                        browseMedia={onFilesBrowseHandler}/>
                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Divider mb={10}/>
                                                        </Grid>
                                                    </Grid>

                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" mb={5}>
                                                                Video
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>

                                                    <VideoCategory
                                                        data={dataObj}
                                                        onChange={(e, field) => {
                                                            handleChange(e);
                                                            inputHandler(e, field)
                                                        }}
                                                        touched={touched}
                                                        errors={errors}
                                                        values={values}
                                                    />
                                                </TabPanel>
                                                <TabPanel value={tabValue} index={3}>
                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" mb={5}>
                                                                Promotions
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <SeoCategory
                                                        data={dataObj}
                                                        onChange={(e, field) => {
                                                            handleChange(e);
                                                            inputHandler(e, field)
                                                        }}
                                                        touched={touched}
                                                        errors={errors}
                                                        values={values}
                                                    />
                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Divider mb={10}/>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={6}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h6" mb={5}>
                                                                Content Elements
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <InlineContentElement
                                                        items={contentElements}
                                                        onAdd={onAddContentElementHandler}
                                                        onUpdate={onContentElementUpdateHandler}
                                                        onDelete={onContentElementRemoveHandler}
                                                        onMediaDelete={onContentElementMediaRemoveHandler}
                                                        onMediaBrowse={onContentElementMediaBrowseHandler}
                                                    />

                                                </TabPanel>

                                            </div>

                                        </form>
                                    )}
                                </Formik>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            }
            {updated &&
                <SnackbarNotification message="Saved successfully" open={updated}/>
            }

            <ConfirmationDialog
                message="Are you sure you want to leave without saving?"
                title="Leave page"
                open={open}
                button="Confirm"
                onConfirm={backToListView}
                onCancel={closeDialog}/>
        </React.Fragment>
    );
}

export default Category;
